import { createContext, useContext } from 'react';

import { EntityValuationActionDefinition } from '@/modules/entities/details/EntityValuation/EntityValuationActions';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { EntityType } from '@/modules/entities/types/EntityType';
import { GroupNodeMember } from '@/modules/estateWaterfall/types';

/** The entity a hovered row/diagram-node toolbar acts on. */
export interface PresentationEntityActionTarget {
  entityId: string;
  entityType: EntityType;
}

/** The group whose member entities the "Group details" drawer lists. */
export interface GroupDetailsRequest {
  members: GroupNodeMember[];
}

/**
 * What the "View source" action should open: the onboarding suggestion's
 * cited-evidence drawer (highlighting the citation), or an entity's
 * source-document drawer.
 * The source-document fallback shows all explicit document associations,
 * regardless of relationship type. Onboarding evidence takes priority.
 *
 * A union rather than an id plus an optional `suggestionId` because the
 * source-document fallback is entity-only — `HouseholdDocumentsViewer` has no
 * query branch for a client organization and throws during render if given one,
 * and its client-profile branch lists household-wide wills/POAs rather than
 * anything linked to that person. Modeling it this way makes the bad request
 * unrepresentable instead of merely avoided.
 */
export type ViewSourceRequest =
  | { kind: 'suggestion'; suggestionId: string }
  | { kind: 'sourceDocuments'; entityId: string };

export interface PresentationEntityActionsContextValue {
  /** Household the presentation belongs to; used to deep-link into the app. */
  householdId: string;
  /**
   * Edit-entity sections to offer for an entity type, in menu order. Returns
   * `[]` when the edit flow isn't wired up (e.g. the viewer can't edit).
   */
  getEditEntitySections: (
    entityType: EntityType
  ) => { label: string; section: EditEntitySection }[];
  /** Open the edit-entity modal for `target` at `section`. */
  onEditEntitySection?: (
    target: PresentationEntityActionTarget,
    section: EditEntitySection
  ) => void;
  /**
   * Run a holdings action (chosen from the row's "Edit holdings" menu) for the
   * entity. The provider fetches the full entity detail by id and mounts the
   * matching modal/flow.
   */
  onEditHoldingsAction?: (
    entityId: string,
    action: EntityValuationActionDefinition
  ) => void;
  /**
   * Open the citation drawer for `request` — the suggestion's cited evidence
   * (mirrors the estate "Locate suggestion" flow) or an entity's source
   * documents, per the request's `kind`.
   */
  onViewSource?: (request: ViewSourceRequest) => void;
  /**
   * Open the sub-trust (testamentary entity) editor for `testamentaryEntityId`.
   * Mounted by the provider outside any diagram's remount-keyed subtree, so an
   * in-place diagram remeasure can't unmount the modal mid-edit.
   */
  onEditSubtrust?: (testamentaryEntityId: string) => void;
  /**
   * Open the client-profile side drawer for an individual (`clientProfileId`).
   * Like the sub-trust editor, mounted by the provider outside any diagram's
   * remount-keyed subtree.
   */
  onEditClientProfile?: (clientProfileId: string) => void;
  /** Open the client-organization side drawer for `organizationId`. */
  onEditClientOrganization?: (organizationId: string) => void;
  /**
   * Open the "Group details" drawer listing a group tile's member entities.
   * Mounted by the provider outside any diagram's remount-keyed subtree, so an
   * in-place diagram remeasure can't unmount the drawer.
   */
  onShowGroupDetails?: (request: GroupDetailsRequest) => void;
}

const PresentationEntityActionsContext =
  createContext<PresentationEntityActionsContextValue | null>(null);

export const PresentationEntityActionsContextProvider =
  PresentationEntityActionsContext.Provider;

/**
 * Row/diagram-node toolbars read their action callbacks from this context,
 * supplied by the slide (via {@link PresentationEntityActionsProvider}). Returns null
 * outside the provider so the host renders plainly (e.g. in tests or
 * non-presentation contexts).
 */
export function usePresentationEntityActionsContext(): PresentationEntityActionsContextValue | null {
  return useContext(PresentationEntityActionsContext);
}
